"use client";

import { useEffect, useState } from "react";
import { addLine } from "@/lib/cart-store";
import { productToItem, trackAddToCart } from "@/lib/analytics";
import { fireCartToast } from "../../components/CartToast";
import { useT } from "../../components/LocaleProvider";
import type { Product } from "../../data/products";
import { formatPrice } from "../../data/products";

type Props = {
  product: Product;
  /** Fires after a successful add — used by the StickyBuyBar modal to close itself. */
  onAdded?: () => void;
};

export function AddToCartPanel({ product, onAdded }: Props) {
  const [selected, setSelected] = useState<number | null>(null);
  const [status, setStatus] = useState<"idle" | "adding" | "added">("idle");
  const t = useT();

  const sizes = product.sizesAvailable;
  const soldOut = sizes.length === 0;
  const priceLabel = formatPrice(product.priceCents);
  const productLabel = `${product.brand} ${product.model} — ${product.colorway}`;

  function handleAdd() {
    if (selected == null || soldOut) return;
    setStatus("adding");
    addLine({ product, size: selected });
    trackAddToCart(productToItem(product));
    fireCartToast({ productName: productLabel, size: selected });
    setStatus("added");
    onAdded?.();
  }

  useEffect(() => {
    if (status !== "added") return;
    const t = setTimeout(() => setStatus("idle"), 2000);
    return () => clearTimeout(t);
  }, [status]);

  return (
    <div className="space-y-6">
      <fieldset>
        <legend className="mono-strip text-[var(--color-bone-dim)]">
          {t({ en: "Select size · US", es: "Elige talla · US" })}
        </legend>
        <div
          role="radiogroup"
          aria-label="Select your US size"
          className="mt-3 grid grid-cols-5 gap-2 sm:grid-cols-6"
        >
          {sizes.map((size) => {
            const active = selected === size;
            return (
              <button
                key={size}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => {
                  setSelected(size);
                  if (status === "added") setStatus("idle");
                }}
                className={[
                  "rounded-sm border px-3 py-2 font-mono text-sm transition",
                  active
                    ? "border-[var(--color-blood)] bg-[var(--color-blood)] text-white"
                    : "border-[var(--color-bone)]/25 text-[var(--color-bone)] hover:border-[var(--color-foil)] hover:text-[var(--color-foil)]",
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]",
                ].join(" ")}
              >
                {size}
              </button>
            );
          })}
          {soldOut && (
            <span className="col-span-full text-sm text-[var(--color-bone-dim)]">
              {t({
                en: "Sold out. DM us — we can source another pair.",
                es: "Agotado. Escríbenos por DM — podemos buscarte otro par.",
              })}
            </span>
          )}
        </div>
      </fieldset>

      <div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={soldOut || selected == null || status === "adding"}
          className="w-full rounded-full bg-[var(--color-blood)] px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
        >
          {status === "added"
            ? t({ en: "Added to bag ✓", es: "Agregado ✓" })
            : selected == null
            ? t({ en: "Pick a size", es: "Elige tu talla" })
            : `${t({ en: "Add to bag", es: "Agregar" })} · ${priceLabel}`}
        </button>
        <p className="mt-3 text-center text-[11px] uppercase tracking-[0.18em] text-[var(--color-bone-dim)]">
          {t({
            en: "Flat $9.99 shipping · Money back on fails",
            es: "Envío plano $9.99 · Reembolso si falla autenticación",
          })}
        </p>
      </div>
    </div>
  );
}
